import type { Identifier } from "ra-core";

import type { SalesProject } from "../../types";
import type { Actor } from "./canAccess";

/**
 * Almikia Epic EL2 (TASK-073): client-side gates of the BPO carousel section (guardias, office
 * hours, 'no disponible' ranges, the project 'Activa' flag, notices). They MIRROR the RLS of
 * TASK-070 (`private.can_edit_project_carousel`, `can_read_project_carousel`,
 * `can_manage_advisor_unavailability`) and never replace it. Vocabulary: "guardia" and
 * "disponibilidad".
 *
 * Who may do what:
 *  - direccion: always (but nothing here is ever deleted except office hours, like the RLS)
 *  - a lead_manager whose brazo reaches `bpo`: edits
 *  - the Team Leader (lider) of THAT project: edits only her project
 *  - an asesora: SEES the section of her own projects only, and writes only her own
 *    'no disponible' ranges
 *  - everyone else (auditoria, administracion, compliance, contabilidad, a lider of another
 *    project): no access
 * Project membership comes from the actor (`memberProjectIds` / `ledProjectIds`, derived from
 * the live `sales_projects`, see `getProjectScope`); without it a check fails closed. An actor
 * without a role gets nothing.
 */
export const CAROUSEL_RESOURCES = [
  // The section as a whole (a project's guardias, office hours and flags).
  "project_carousel",
  "project_office_hours",
  "project_guardias",
  "project_guardias_roster",
  // The project-level 'Activa' flag (`lead_assignment_eligible_advisors`).
  "project_advisor_flags",
  "advisor_unavailability",
  // Notices are written by the follow-up job only; clients just read them.
  "lead_carousel_notices",
] as const;

export const isCarouselResource = (resource: string | undefined): boolean =>
  CAROUSEL_RESOURCES.some((name) => name === resource);

interface GateParams {
  action: string;
  resource: string;
  record?: Record<string, any>;
}

const READ = ["list", "show"];
const WRITE = ["create", "edit"];

const hasId = (ids: readonly Identifier[] | undefined, id: unknown) =>
  id != null && (ids ?? []).some((value) => String(value) === String(id));

/** Mirrors `private.in_lead_manager_scope_arm('bpo')`: a null brazo reaches nothing. */
const isBpoLeadManager = (actor: Actor) =>
  actor.role === "lead_manager" &&
  (actor.business_arm === "bpo" ||
    actor.business_arm === "ambos" ||
    actor.business_arm === "direccion_general");

const isCommercialAdvisor = (actor: Actor) =>
  actor.role === "asesora" || actor.role === "lider";

/**
 * Direccion, a bpo-scope lead_manager, or the Team Leader of this very project. Also the scope
 * of the payment-plan edits (`private.can_edit_project_plans`, Almikia batch EQ3, TASK-113).
 */
export const canEditProject = (actor: Actor, projectId: unknown) => {
  if (actor.role === "direccion" || isBpoLeadManager(actor)) return true;
  if (actor.role !== "lider") return false;
  // No project to compare with (a section-level check): she may edit if she leads any.
  return projectId == null
    ? (actor.ledProjectIds?.length ?? 0) > 0
    : hasId(actor.ledProjectIds, projectId);
};

/** Every editor plus the active members of the project. */
const canReadProject = (actor: Actor, projectId: unknown) => {
  if (canEditProject(actor, projectId)) return true;
  if (!isCommercialAdvisor(actor)) return false;
  return projectId == null
    ? (actor.memberProjectIds?.length ?? 0) > 0
    : hasId(actor.memberProjectIds, projectId);
};

/**
 * 'No disponible' ranges: the advisor herself, or whoever may view her availability. The
 * record carries the advisor (`sales_id`); a Team Leader reaches the active members of the
 * projects she leads (`ledTeamSalesIds`, like `private.shares_led_project`).
 */
const canManageUnavailability = (
  actor: Actor,
  record?: GateParams["record"],
) => {
  if (actor.role === "direccion" || isBpoLeadManager(actor)) return true;
  if (!isCommercialAdvisor(actor)) return false;
  if (record?.sales_id == null) return true;
  if (actor.id != null && String(record.sales_id) === String(actor.id)) {
    return true;
  }
  return (
    actor.role === "lider" && hasId(actor.ledTeamSalesIds, record.sales_id)
  );
};

/**
 * The gate. `record` carries the row being read or written (`project_id`, plus `sales_id`
 * for the unavailability and the notices); without it the check is section-level.
 */
export const canAccessCarousel = (
  actor: Actor | undefined,
  params: GateParams,
): boolean => {
  if (!actor?.role) return false;
  const { action, resource, record } = params;
  const projectId = record?.project_id;

  // Nothing here is exported, and only office hours are ever deleted (the RLS has no delete
  // policy on the other tables: guardias and flags end or switch, ranges are cancelled).
  if (action === "export") return false;
  if (action === "delete") {
    return (
      resource === "project_office_hours" && canEditProject(actor, projectId)
    );
  }

  switch (resource) {
    case "project_carousel":
      if (READ.includes(action)) return canReadProject(actor, projectId);
      return action === "edit" && canEditProject(actor, projectId);
    case "project_guardias_roster":
    case "lead_carousel_notices":
      if (!READ.includes(action)) return false;
      if (
        resource === "lead_carousel_notices" &&
        actor.id != null &&
        record?.sales_id != null &&
        String(record.sales_id) === String(actor.id)
      ) {
        // The notified advisor reads her own notice.
        return true;
      }
      return resource === "lead_carousel_notices"
        ? canEditProject(actor, projectId)
        : canReadProject(actor, projectId);
    case "project_office_hours":
    case "project_guardias":
    case "project_advisor_flags":
      if (READ.includes(action)) return canReadProject(actor, projectId);
      return WRITE.includes(action) && canEditProject(actor, projectId);
    case "advisor_unavailability":
      if (READ.includes(action) || WRITE.includes(action)) {
        return canManageUnavailability(actor, record);
      }
      return false;
    default:
      return false;
  }
};

/**
 * The live project scope of an actor, from the `sales_projects` rows: the projects where she is
 * an active member and those she leads (Team Leader). Spread it into the `Actor` before
 * calling `canAccess`.
 */
export const getProjectScope = (
  actorId: Identifier | undefined,
  salesProjects: readonly Pick<
    SalesProject,
    "sales_id" | "project_id" | "is_lider_for_project" | "ended_at"
  >[],
): Pick<Actor, "memberProjectIds" | "ledProjectIds" | "ledTeamSalesIds"> => {
  const active = salesProjects.filter((row) => !row.ended_at);
  const own = active.filter(
    (row) => actorId != null && String(row.sales_id) === String(actorId),
  );
  const ledProjectIds = own
    .filter((row) => row.is_lider_for_project)
    .map((row) => row.project_id);
  return {
    memberProjectIds: own.map((row) => row.project_id),
    ledProjectIds,
    // Mirrors `private.shares_led_project`: the active members of the projects she leads.
    ledTeamSalesIds: active
      .filter((row) => hasId(ledProjectIds, row.project_id))
      .map((row) => row.sales_id),
  };
};
